import { API_BASE_URL } from './api';
import { UserRole } from '../types/auth';

export type OrderSignatureStatus = 
    | 'DRAFT' 
    | 'PENDING_SIGNATURE' 
    | 'SIGNED_BY_BUYER' 
    | 'SIGNED_BY_SUPPLIER' 
    | 'SIGNED_BY_BOTH' 
    | 'CANCELLED';

export interface OrderSignature {
    id: number;
    orderId: string;
    orderGuid?: string;
    status: OrderSignatureStatus;
    statusDisplay: string;
    companyBin: string;
    companyName: string;
    generatedPdfPath?: string;
    invoicePdfPath?: string;
    sellingPdfPath?: string;
    buyerSignedAt?: string;
    supplierSignedAt?: string;
    cmsZipFilePath?: string;
    createdAt: string;
    updatedAt: string;
    isSignedByBuyer: boolean;
    isSignedBySupplier: boolean;
    isSigned: boolean;
    hasInvoice?: boolean;
    hasSelling?: boolean;
}

export interface SignOrderRequest {
    signatureData: string;
    certificateData: string;
    signerName: string;
    organizationName: string;
}

export interface ApiResponse<T = any> {
    success: boolean;
    message?: string;
    data?: T;
}

class OrderSignatureService {
    private getAuthHeaders(): HeadersInit {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
        };
    }

    /**
     * Получить заявку по ID
     */
    async getOrderSignature(id: number): Promise<OrderSignature> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка получения заявки' }));
            throw new Error(errorData.message || 'Ошибка получения заявки');
        }

        const result: ApiResponse<OrderSignature> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка получения заявки');
        }

        return result.data;
    }

    /**
     * Получить заявку по orderId
     */
    async getOrderSignatureByOrderId(orderId: string): Promise<OrderSignature> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/by-order/${orderId}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка получения заявки' }));
            throw new Error(errorData.message || 'Ошибка получения заявки');
        }

        const result: ApiResponse<OrderSignature> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка получения заявки');
        }

        return result.data;
    }

    /**
     * Получить все заявки текущего пользователя
     */
    async getMyOrderSignatures(): Promise<OrderSignature[]> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/my`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка получения заявок' }));
            throw new Error(errorData.message || 'Ошибка получения заявок');
        }

        const result: ApiResponse<OrderSignature[]> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка получения заявок');
        }

        return result.data;
    }

    /**
     * Создать заявку на подписание для заказа
     */
    async createOrderSignature(orderId: string): Promise<OrderSignature> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/create/${orderId}`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка создания заявки' }));
            throw new Error(errorData.message || 'Ошибка создания заявки');
        }

        const result: ApiResponse<OrderSignature> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка создания заявки');
        }

        return result.data;
    }

    /**
     * Получить все заявки компании (для админа/модератора)
     */
    async getCompanyOrderSignatures(companyBin: string): Promise<OrderSignature[]> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/company/${companyBin}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка получения заявок компании' }));
            throw new Error(errorData.message || 'Ошибка получения заявок компании');
        }

        const result: ApiResponse<OrderSignature[]> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка получения заявок компании');
        }

        return result.data;
    }

    /**
     * Скачать PDF заявки
     */
    async downloadOrderSignaturePdf(id: number, orderId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        if (!response.ok) {
            throw new Error('Ошибка скачивания заявки');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `order-sign-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    /**
     * Скачать счёт на оплату
     */
    async downloadInvoice(id: number, orderId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/download-invoice`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка скачивания счёта' }));
            throw new Error(errorData.message || 'Ошибка скачивания счёта');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    /**
     * Скачать накладную
     */
    async downloadSelling(id: number, orderId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/download-selling`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка скачивания накладной' }));
            throw new Error(errorData.message || 'Ошибка скачивания накладной');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `selling-${orderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    /**
     * Повторно запросить накладную из 1С
     */
    async refetchSelling(id: number): Promise<string> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/refetch-selling`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка запроса накладной' }));
            throw new Error(errorData.message || 'Ошибка запроса накладной');
        }

        const result: ApiResponse<{ sellingUrl: string }> = await response.json();
        if (!result.success) {
            throw new Error(result.message || 'Ошибка запроса накладной');
        }

        return result.data?.sellingUrl || '';
    }

    /**
     * Скачать CMS архив с подписями
     */
    async downloadCmsZip(id: number, orderId: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/download-cms`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка скачивания CMS архива' }));
            throw new Error(errorData.message || 'Ошибка скачивания CMS архива');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cms-${orderId}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    /**
     * Повторно запросить счёт из 1С
     */
    async refetchInvoice(id: number): Promise<string> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/refetch-invoice`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка запроса счёта' }));
            throw new Error(errorData.message || 'Ошибка запроса счёта');
        }

        const result: ApiResponse<{ invoiceUrl: string }> = await response.json();
        if (!result.success) {
            throw new Error(result.message || 'Ошибка запроса счёта');
        }

        return result.data?.invoiceUrl || '';
    }

    /**
     * Подписать заявку покупателем
     */
    async signByBuyer(id: number, request: SignOrderRequest): Promise<OrderSignature> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/sign-buyer`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка подписания заявки' }));
            throw new Error(errorData.message || 'Ошибка подписания заявки');
        }

        const result: ApiResponse<OrderSignature> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка подписания заявки');
        }

        return result.data;
    }

    /**
     * Подписать заявку поставщиком (для админа/модератора)
     */
    async signBySupplier(id: number, request: SignOrderRequest): Promise<OrderSignature> {
        const response = await fetch(`${API_BASE_URL}/order-signatures/${id}/sign-supplier`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Ошибка подписания заявки' }));
            throw new Error(errorData.message || 'Ошибка подписания заявки');
        }

        const result: ApiResponse<OrderSignature> = await response.json();
        if (!result.success || !result.data) {
            throw new Error(result.message || 'Ошибка подписания заявки');
        }

        return result.data;
    }

    /**
     * Получить статус заявки для отображения
     */
    getStatusColor(status: OrderSignatureStatus): string {
        switch (status) {
            case 'DRAFT':
                return 'gray';
            case 'PENDING_SIGNATURE':
                return 'blue';
            case 'SIGNED_BY_BUYER':
                return 'orange';
            case 'SIGNED_BY_SUPPLIER':
                return 'orange';
            case 'SIGNED_BY_BOTH':
                return 'green';
            case 'CANCELLED':
                return 'red';
            default:
                return 'gray';
        }
    }

    /**
     * Проверить, нужно ли подписывать заявку
     */
    needsSignature(orderSignature: OrderSignature, userRole: UserRole): boolean {
        if (userRole === UserRole.CLIENT && !orderSignature.isSignedByBuyer) {
            return orderSignature.status === 'PENDING_SIGNATURE';
        }
        if ((userRole === UserRole.SUPER_ADMIN || userRole === UserRole.MODERATOR) && 
            orderSignature.isSignedByBuyer && !orderSignature.isSignedBySupplier) {
            return orderSignature.status === 'SIGNED_BY_BUYER';
        }
        return false;
    }
}

export const orderSignatureService = new OrderSignatureService();
export default orderSignatureService;

